import { FC, ReactNode, useState } from "react";
import type { UseMutationResult } from "@tanstack/react-query";
import CheckoutSkeleton from "./checkout-skeleton";
import CheckoutLayout from "./checkout-layout";
import OrderSummary from "./order-summary";
import PaymentPanel from "./payment-panel";
import SuccessScreen from "./success-screen";
import AlreadyPaidScreen from "./already-paid-screen";
import ErrorScreen from "./error-screen";
import PaymentUnavailableScreen from "./payment-unavailable-screen";
import type {
  CheckoutCopy,
  CheckoutData,
  OrderSummaryModel,
  PayPayload,
} from "../types";

type TerminalView = "success" | "already-paid";

interface Props {
  isLoading: boolean;
  error: unknown;
  data?: CheckoutData;
  summary?: OrderSummaryModel;
  summaryNote?: ReactNode;
  payMutation: UseMutationResult<void, Error, PayPayload>;
  copy: CheckoutCopy;
}

// Shared, flow-agnostic checkout screen. Feature pages fetch + normalize their
// own data and hand it here; everything below is identical across flows.
const CheckoutView: FC<Props> = ({
  isLoading,
  error,
  data,
  summary,
  summaryNote,
  payMutation,
  copy,
}) => {
  const [view, setView] = useState<TerminalView | null>(null);

  if (isLoading) return <CheckoutSkeleton />;
  if (view === "success")
    return <SuccessScreen title={copy.successTitle} description={copy.successDescription} />;
  if (view === "already-paid")
    return (
      <AlreadyPaidScreen
        title={copy.alreadyPaidTitle}
        description={copy.alreadyPaidDescription}
      />
    );
  if (error || !data || !summary) return <ErrorScreen error={error} />;

  if (data.settled) {
    return (
      <AlreadyPaidScreen
        title={copy.alreadyPaidTitle}
        description={copy.alreadyPaidDescription}
      />
    );
  }

  if (!data.paymentAvailable) {
    return (
      <PaymentUnavailableScreen
        companyName={data.company.name}
        companyEmail={data.company.email}
        companyPhone={data.company.phone}
        companyLogo={data.company.logo}
        subtitle={copy.unavailableSubtitle}
        description={copy.unavailableDescription}
        reference={copy.unavailableReference}
      />
    );
  }

  return (
    <CheckoutLayout>
      <OrderSummary summary={summary} note={summaryNote} />
      <PaymentPanel
        gatewayConfig={data.gatewayConfig}
        paymentMethods={data.paymentMethods}
        billingInfo={data.billingInfo}
        contractId={data.contractId}
        totalAmount={summary.total}
        invalidateKey={data.invalidateKey}
        payMutation={payMutation}
        onSuccess={() => setView("success")}
        onAlreadyPaid={() => setView("already-paid")}
      />
    </CheckoutLayout>
  );
};

export default CheckoutView;
